import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Paper,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ShieldAlert,
  Clock,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';

export interface PolarisFinding {
  id: string | null;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'unknown';
  originalSeverity?: string | null;
  type: string;
  cwe?: string | null;
  language?: string | null;
  filename?: string | null;
  location?: string | null;
  line?: string | number | null;
  title?: string | null;
  description?: string | null;
  remediation?: string | null;
  toolType?: string | null;
  projectId?: string | null;
  projectName?: string | null;
  repoSlug?: string | null;
}

export interface PolarisProjectEntry {
  projectId: string;
  projectName: string | null;
  repoName: string;
  repoSlug: string | null;
  matched: boolean;
  totalCount: number;
  truncated: boolean;
  severityCounts: { critical: number; high: number; medium: number; low: number; unknown: number };
  findings: PolarisFinding[];
  error?: string;
}

export interface PolarisData {
  applicationId: string;
  applicationName?: string | null;
  applicationUrl?: string | null;
  fetchedAt: string;
  totalCount: number;
  truncated: boolean;
  severityCounts: { critical: number; high: number; medium: number; low: number; unknown: number };
  findings: PolarisFinding[];
  projects?: PolarisProjectEntry[];
  unmatchedProjects?: string[];
  error?: string;
}

const PAGE_SIZE = 25;

// ============================================================
// Polaris Section — application-level findings (not per-repo).
// Display-only: Polaris does not contribute to Sentinel's KPI/security score.
// ============================================================
function PolarisSection({
  loading, data, configured, storedAt, status, fetchError, onRetry, emptyMessage,
}: {
  loading: boolean;
  data: PolarisData | null;
  configured: boolean;
  storedAt: string | null;
  status?: string;
  fetchError?: string | null;
  onRetry?: () => void;
  emptyMessage?: string;
}) {
  const theme = useTheme();
  const [selectedSeverity, setSelectedSeverity] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Reset severity selection and pagination whenever the report changes.
  useEffect(() => {
    setSelectedSeverity(null);
    setVisibleCount(PAGE_SIZE);
  }, [data?.applicationId, data?.fetchedAt]);

  // Reset pagination whenever the selected severity changes.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [selectedSeverity]);

  const severityColor = (s: string) => {
    switch ((s || '').toLowerCase()) {
      case 'critical': return '#ef4444';
      case 'high': return '#f97316';
      case 'medium': return '#f59e0b';
      case 'low': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  const statusLabel = (s?: string) => {
    switch (s) {
      case 'running': return { label: 'Scan running…', color: 'info' as const };
      case 'completed': return { label: 'Scan completed', color: 'success' as const };
      case 'failed': return { label: 'Scan failed', color: 'error' as const };
      case 'skipped': return { label: 'Scan skipped', color: 'default' as const };
      case 'queued': return { label: 'Scan queued', color: 'warning' as const };
      default: return null;
    }
  };

  if (!configured) {
    return (
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <ShieldAlert size={32} style={{ color: theme.palette.text.disabled, marginBottom: 8 }} />
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 0.5 }}>Polaris not configured</Typography>
        <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
          Add the project's Polaris application UUID in Project Settings → Connections to see findings here.
        </Typography>
      </Box>
    );
  }

  if (loading && !data) {
    return (
      <Box sx={{ py: 4, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <CircularProgress size={18} thickness={5} />
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Fetching Polaris data…</Typography>
      </Box>
    );
  }

  if (!data) {
    if (fetchError) {
      return (
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ShieldAlert size={14} style={{ color: theme.palette.error.main }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Couldn't load the Polaris report</Typography>
          </Stack>
          <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 0.5 }}>{fetchError}</Typography>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
            {onRetry && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshCw size={13} />}
                onClick={onRetry}
                sx={{ textTransform: 'none', fontSize: 12 }}
              >
                Retry
              </Button>
            )}
            {statusLabel(status) && (
              <Chip
                label={statusLabel(status)?.label}
                size="small"
                color={statusLabel(status)?.color}
                sx={{ fontSize: 11, height: 22 }}
              />
            )}
          </Stack>
        </Paper>
      );
    }
    return (
      <Stack spacing={1}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>No Polaris data available yet.</Typography>
        {statusLabel(status) && (
          <Chip
            label={statusLabel(status)?.label}
            size="small"
            color={statusLabel(status)?.color}
            sx={{ alignSelf: 'flex-start', fontSize: 11, height: 22 }}
          />
        )}
      </Stack>
    );
  }

  if (data.error) {
    return (
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ShieldAlert size={14} style={{ color: theme.palette.error.main }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Failed to fetch Polaris findings</Typography>
        </Stack>
        <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 0.5 }}>{data.error}</Typography>
        {statusLabel(status) && (
          <Chip
            label={statusLabel(status)?.label}
            size="small"
            color={statusLabel(status)?.color}
            sx={{ mt: 1, fontSize: 11, height: 22 }}
          />
        )}
      </Paper>
    );
  }

  const severityGroups = [
    { key: 'critical', label: 'Critical', count: data.severityCounts.critical, color: '#ef4444' },
    { key: 'high', label: 'High', count: data.severityCounts.high, color: '#f97316' },
    { key: 'medium', label: 'Medium', count: data.severityCounts.medium, color: '#f59e0b' },
    { key: 'low', label: 'Low', count: data.severityCounts.low, color: '#3b82f6' },
    { key: 'unknown', label: 'Unknown', count: data.severityCounts.unknown, color: '#6b7280' },
  ].filter((g) => g.count > 0 || g.key !== 'unknown');

  const findingsForSelected = selectedSeverity
    ? data.findings.filter((f) => f.severity === selectedSeverity)
    : [];
  const visibleFindings = findingsForSelected.slice(0, visibleCount);

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
        {data.applicationName && (
          <Chip label={data.applicationName} size="small" sx={{ fontSize: 10, height: 20 }} />
        )}
        <Typography sx={{ fontSize: 11, color: 'text.disabled', fontFamily: 'monospace' }}>{data.applicationId}</Typography>
        {data.applicationUrl && (
          <Button
            size="small"
            variant="text"
            href={data.applicationUrl}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<ExternalLink size={11} />}
            sx={{ textTransform: 'none', fontSize: 11, minWidth: 'auto', px: 0.75, py: 0 }}
          >
            Open in Polaris
          </Button>
        )}
        {statusLabel(status) && (
          <Chip
            label={statusLabel(status)?.label}
            size="small"
            color={statusLabel(status)?.color}
            sx={{ fontSize: 11, height: 22 }}
          />
        )}
        {(storedAt || data.fetchedAt) && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: 'auto !important' }}>
            <Clock size={12} style={{ color: theme.palette.text.disabled }} />
            <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
              Last fetched: {new Date(storedAt || data.fetchedAt).toLocaleString()}
              {loading && <> &nbsp;<CircularProgress size={10} thickness={5} sx={{ ml: 0.5, verticalAlign: 'middle' }} /></>}
            </Typography>
          </Stack>
        )}
      </Stack>

      {data.truncated && (
        <Chip
          {...(data.applicationUrl
            ? { component: 'a' as const, href: data.applicationUrl, target: '_blank', rel: 'noopener noreferrer', clickable: true }
            : {})}
          label={data.applicationUrl
            ? `Showing ${data.findings.length} of ${data.totalCount} findings (severity counts reflect the fetched subset) — open in Polaris to narrow down`
            : `Showing ${data.findings.length} of ${data.totalCount} findings (severity counts reflect the fetched subset) — narrow down in Polaris for the full list`}
          size="small"
          variant="outlined"
          color="warning"
          sx={{ fontSize: 11, alignSelf: 'flex-start' }}
        />
      )}

      {(data.unmatchedProjects?.length ?? 0) > 0 && (
        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
          {data.unmatchedProjects!.length} Polaris project{data.unmatchedProjects!.length === 1 ? '' : 's'} not mapped to a repository: {data.unmatchedProjects!.join(', ')}
        </Typography>
      )}

      {data.findings.length === 0 ? (
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{emptyMessage || 'No open Polaris findings for this application.'}</Typography>
        </Paper>
      ) : (
        <>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {severityGroups.map(({ key, label, count, color }) => {
              const isSelected = selectedSeverity === key;
              const hasFindings = count > 0;
              return (
                <Button
                  key={key}
                  onClick={() => hasFindings && setSelectedSeverity(isSelected ? null : key)}
                  disabled={!hasFindings}
                  aria-expanded={isSelected}
                  aria-controls={`polaris-findings-${key}`}
                  sx={{
                    borderRadius: 1.5,
                    cursor: hasFindings ? 'pointer' : 'default',
                    border: '1px solid',
                    borderColor: isSelected ? color : alpha(color, 0.3),
                    bgcolor: isSelected ? alpha(color, 0.1) : alpha(color, 0.04),
                    px: 1.25, py: 0.5,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    minWidth: 'auto',
                    '&:hover': hasFindings ? { bgcolor: alpha(color, 0.12) } : undefined,
                    '&.Mui-disabled': { opacity: 0.5, color: 'inherit' },
                  }}
                >
                  <Typography sx={{ fontSize: 18, fontWeight: 700, color, lineHeight: 1 }}>{count}</Typography>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 600, color, lineHeight: 1.2 }}>{label}</Typography>
                    {hasFindings && (
                      <Stack direction="row" alignItems="center" spacing={0.25}>
                        <Typography sx={{ fontSize: 9, color: 'text.disabled' }}>{isSelected ? 'collapse' : 'expand'}</Typography>
                        {isSelected ? <ChevronUp size={10} style={{ color: theme.palette.text.disabled }} /> : <ChevronDown size={10} style={{ color: theme.palette.text.disabled }} />}
                      </Stack>
                    )}
                  </Box>
                </Button>
              );
            })}
          </Stack>

          {selectedSeverity && (
            <Collapse in={!!selectedSeverity} unmountOnExit id={`polaris-findings-${selectedSeverity}`}>
              <Box sx={{ borderRadius: 2, border: '1px solid', borderColor: alpha(severityColor(selectedSeverity), 0.25), overflow: 'hidden' }}>
                <Box sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: alpha(severityColor(selectedSeverity), 0.15), bgcolor: alpha(severityColor(selectedSeverity), 0.06) }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: severityColor(selectedSeverity) }}>
                    {selectedSeverity.charAt(0).toUpperCase() + selectedSeverity.slice(1)} — {visibleFindings.length} of {findingsForSelected.length} shown
                  </Typography>
                </Box>
                {visibleFindings.map((finding, idx) => (
                  <Box
                    key={finding.id || idx}
                    sx={{
                      px: 2, py: 1.25,
                      borderBottom: idx < visibleFindings.length - 1 ? '1px solid' : 'none',
                      borderColor: alpha(severityColor(selectedSeverity), 0.1),
                      bgcolor: idx % 2 === 0 ? 'transparent' : alpha(theme.palette.action.hover, 0.3),
                    }}
                  >
                    <Stack direction="row" alignItems="flex-start" spacing={1} flexWrap="wrap">
                      <Chip
                        label={finding.severity.toUpperCase()}
                        size="small"
                        sx={{ fontSize: 9, height: 18, bgcolor: alpha(severityColor(finding.severity), 0.12), color: severityColor(finding.severity), fontWeight: 700, flexShrink: 0 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 500, mb: 0.25 }}>
                          {finding.title || finding.type}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                          {(finding.filename || finding.location) && (
                            <Typography sx={{ fontSize: 11, fontFamily: 'monospace', color: 'text.secondary' }}>
                              {finding.filename || finding.location}{finding.line != null ? `:${finding.line}` : ''}
                            </Typography>
                          )}
                          {finding.projectName && (data.projects?.length ?? 0) > 1 && (
                            <Chip label={finding.projectName} size="small" sx={{ fontSize: 9, height: 16, bgcolor: alpha(theme.palette.text.secondary, 0.08), color: 'text.secondary' }} />
                          )}
                          {finding.cwe && (
                            <Chip label={finding.cwe} size="small" sx={{ fontSize: 9, height: 16, fontFamily: 'monospace', bgcolor: alpha(theme.palette.info.main, 0.07), color: theme.palette.info.main }} />
                          )}
                          {finding.toolType && (
                            <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>{finding.toolType}</Typography>
                          )}
                        </Stack>
                        {finding.description && (
                          <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.5 }}>
                            {finding.description.length > 220 ? `${finding.description.substring(0, 220)}…` : finding.description}
                          </Typography>
                        )}
                        {finding.remediation && (
                          <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.25 }}>
                            Remediation: {finding.remediation.length > 160 ? `${finding.remediation.substring(0, 160)}…` : finding.remediation}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </Box>
                ))}
              </Box>

              {visibleCount < findingsForSelected.length && (
                <Button size="small" variant="outlined" onClick={() => setVisibleCount((v) => v + PAGE_SIZE)} sx={{ alignSelf: 'flex-start', textTransform: 'none' }}>
                  Show more ({findingsForSelected.length - visibleCount} remaining)
                </Button>
              )}
            </Collapse>
          )}
        </>
      )}
    </Stack>
  );
}

export default PolarisSection;
